import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@savvy-kit/components/dialog';

import type { TrackedParentElementAddedProps } from '@savvy/deprecated-shared/tracking/parent-element-view-context';
import { ParentElementViewProvider } from '@savvy/deprecated-shared/tracking/parent-element-view-context';
import { useTrackElementView } from '@savvy/deprecated-shared/tracking/use-track-element-view';

export type TrackedDialogProps = TrackedParentElementAddedProps &
  Omit<ComponentProps<typeof Dialog>, 'open' | 'onOpenChange' | 'children'> & {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly children: ReactNode;
    /** Panel classes — width/height, plus `z-[1400]` when nested inside a surviving MUI dialog. */
    readonly className?: string;
    readonly overlayClassName?: string;
    readonly showCloseButton?: boolean;
    readonly portalContainer?: ComponentProps<typeof DialogContent>['portalContainer'];
    /**
     * Point at a `tabIndex={-1}` title ref when the first focusable child opens something on focus
     * (a combobox dropdown would cover the footer) — `false` keeps focus outside the dialog entirely.
     */
    readonly initialFocus?: ComponentProps<typeof DialogContent>['initialFocus'];
    /**
     * Use `size="fullscreen"` rather than hand-rolling `h-screen w-screen max-w-none` in
     * `className` — a bare `max-w-none` does not beat the panel's base `sm:max-w-xl`
     * (tailwind-merge only resolves conflicts within the same modifier), so the panel
     * stays capped at 576px from the `sm` breakpoint up.
     */
    readonly size?: ComponentProps<typeof DialogContent>['size'];
  };

// z-[1400] + overlayClassName="z-[1400]" required when nested inside a surviving MUI dialog to win the stacking context.
export default function TrackedDialog({
  elementName,
  open,
  onOpenChange,
  children,
  className,
  overlayClassName,
  showCloseButton,
  portalContainer,
  initialFocus,
  size,
  ...dialogRootProps
}: TrackedDialogProps) {
  const onElementHidden = useTrackElementView({ elementName, elementType: 'dialog', isVisible: open });

  // Record view-duration when an open dialog goes away — via open→false OR via
  // unmount (many consumers render `{show && <TrackedDialog open .../>}` and close
  // by unmounting, so `open` never flips). Cleanup only runs for an `open` render,
  // so a consumer-vetoed close (open stays true) isn't counted.
  const onElementHiddenRef = useRef(onElementHidden);
  onElementHiddenRef.current = onElementHidden;
  useEffect(() => {
    if (!open) return;
    return () => onElementHiddenRef.current();
  }, [open]);

  return (
    <Dialog {...dialogRootProps} open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={className}
        overlayClassName={overlayClassName}
        showCloseButton={showCloseButton}
        portalContainer={portalContainer}
        initialFocus={initialFocus}
        size={size}
      >
        <ParentElementViewProvider parentElementName={elementName} parentElementType="dialog">
          {children}
        </ParentElementViewProvider>
      </DialogContent>
    </Dialog>
  );
}
